import { useState } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import type { BatteryPlanDataPoint } from '../../types';
import { PLAN_COLORS } from '../../utils/chartColors';
import { regionToTimezone, formatTimeInTz, getLocalHourInTz } from '../../utils/timezone';

type SeriesKey = 'windows' | 'batteryPower' | 'evPower' | 'soc' | 'solar' | 'buyPrice' | 'sellPrice';

interface BatteryPlanChartProps {
  data: BatteryPlanDataPoint[];
  className?: string;
  // Optimizer aggressiveness preset for the site. Determines the deadband
  // around zero where the controller leaves the inverter in Regular Mode
  // — and therefore which intervals get rendered as BUY/SELL windows.
  // Defaults to "balanced" if not provided.
  optimizationMode?: string;
  // Rated discharge power of the inverter (battery_config.max_discharge_w, W).
  // Above ~6 kW the controller widens the deadband to half of this, so the
  // chart must do the same or its SELL windows would show commands the
  // controller never sends.
  ratedDischargeW?: unknown;
  // Site price provider. For "globird_zerohero" we overlay the plan's named
  // GloBird windows (free charging + self-consumption 6-9pm).
  priceProvider?: string;
  // Site NEM region (e.g. "VIC1"); used to render times in the site's timezone
  // and to place the GloBird windows at the correct local hours.
  region?: string;
  // GloBird ZeroCharge free-charging window in site-local hours (fractional).
  // Per-site setting; defaults to the plan's current 12pm-3pm window.
  freeChargeWindow?: { startHour: number; endHour: number };
  // Flow Power Happy Hour feed-in window in site-local hours (fractional),
  // shaded when priceProvider is "flowpower_happy_hour".
  happyHourWindow?: { startHour: number; endHour: number };
}

interface Window {
  startIndex: number;
  endIndex: number;
  type: 'buy' | 'sell' | 'sell-excess';
}

// Fixed-plan named windows (GloBird ZeroHero, Flow Power Happy Hour), in
// site-local hours.
type NamedWindowType = 'free-charge' | 'self-consumption' | 'happy-hour';

interface NamedWindow {
  startIndex: number;
  endIndex: number;
  type: NamedWindowType;
}

interface NamedWindowSpec {
  type: NamedWindowType;
  startHour: number;
  endHour: number;
}

// Default ZeroCharge window (current plan, 12pm-3pm); the per-site setting
// arrives via the freeChargeWindow prop. Self-consumption is fixed by the plan.
const GLOBIRD_DEFAULT_FREE_CHARGE = { startHour: 12, endHour: 15 };
const GLOBIRD_SELF_CONSUMPTION = { start: 18, end: 21 }; // 6-9pm super-export/no-import
const FLOWPOWER_DEFAULT_HAPPY_HOUR = { startHour: 17.5, endHour: 21.5 }; // 5:30-9:30pm

/**
 * Find contiguous runs of intervals whose site-local hour falls in the plan's
 * named windows (first matching spec wins), for shading on the chart.
 */
function identifyNamedWindows(
  chartData: { timestamp: string }[],
  tz: string,
  specs: NamedWindowSpec[],
): NamedWindow[] {
  const windows: NamedWindow[] = [];
  let current: NamedWindow | null = null;
  chartData.forEach((point, i) => {
    const hour = getLocalHourInTz(point.timestamp, tz);
    const type =
      specs.find((w) => hour >= w.startHour && hour < w.endHour)?.type ?? null;
    if (type) {
      if (current && current.type === type) {
        current.endIndex = i;
      } else {
        if (current) windows.push(current);
        current = { startIndex: i, endIndex: i, type };
      }
    } else if (current) {
      windows.push(current);
      current = null;
    }
  });
  if (current) windows.push(current);
  return windows;
}

// Mirror of services/battery_controller/main.py:_THRESHOLD_BY_MODE_W.
// Keep these two tables in sync — the chart's BUY/SELL windows are
// supposed to show what the controller would actually do, and that's
// only true if the same threshold is applied here.
const THRESHOLD_BY_MODE_W: Record<string, number> = {
  self_consumption: 1500,
  balanced: 1000,
  maximum_profit: 500,
};
const DEFAULT_THRESHOLD_W = 1000;
// Mirror of RATED_POWER_THRESHOLD_* in the controller. The presets were tuned
// on ~5-6 kW inverters; Sell/Charge Mode runs at the inverter's full rating,
// so on bigger units the controller lifts the deadband to a fifth of the
// rated discharge power (battery_config.max_discharge_w) when that is larger
// (the overshoot is bounded by the controller's slot-target stop).
const RATED_POWER_THRESHOLD_BASELINE_W = 6000;
const RATED_POWER_THRESHOLD_FRACTION = 0.2;
// Power below this (in kW) during a grid-export interval means the battery
// isn't actually discharging — the export is solar surplus that the
// inverter handles in Regular Mode. Kept as a UX-only distinction
// ("SELL EXCESS") so users can tell at a glance which exports are
// controller-issued sell commands vs. passive solar dump.
const SELL_EXCESS_BATTERY_KW = 0.1;

export function thresholdKwForMode(
  mode: string | undefined,
  ratedDischargeW?: unknown,
): number {
  let presetW = DEFAULT_THRESHOLD_W;
  if (mode && THRESHOLD_BY_MODE_W[mode] !== undefined) {
    presetW = THRESHOLD_BY_MODE_W[mode];
  }
  const ratedW = Number(ratedDischargeW);
  if (Number.isFinite(ratedW) && ratedW > RATED_POWER_THRESHOLD_BASELINE_W) {
    return Math.max(presetW, Math.round(ratedW * RATED_POWER_THRESHOLD_FRACTION)) / 1000;
  }
  return presetW / 1000;
}

/**
 * Classify a single interval the same way the battery controller does.
 * Mirrors services/battery_controller/main.py:_determine_mode.
 *   P_batt > +TH AND P_grid < -TH → 'sell'  (Sell Mode / discharge)
 *   P_batt < -TH AND P_grid > +TH → 'buy'   (Charge Mode / grid charge)
 *   otherwise                      → null    (Regular Mode / auto)
 *
 * The chart adds one extra category — 'sell-excess' — for intervals where
 * grid is exporting but the battery isn't actually discharging. The
 * controller treats this as 'auto', but it's useful info for users so we
 * surface it as its own UX-only window type.
 */
function classifyInterval(
  batteryKw: number,
  gridKw: number,
  thresholdKw: number,
): 'buy' | 'sell' | 'sell-excess' | null {
  if (batteryKw > thresholdKw && gridKw < -thresholdKw) return 'sell';
  if (batteryKw < -thresholdKw && gridKw > thresholdKw) return 'buy';
  // Solar surplus passing straight through to the grid: not a controller
  // mode change, but a noteworthy state.
  if (gridKw < -thresholdKw && batteryKw < SELL_EXCESS_BATTERY_KW) {
    return 'sell-excess';
  }
  return null;
}

/**
 * Identify contiguous buy/sell windows from per-interval classifications.
 *
 * Process:
 *   1. Walk the data, grouping consecutive same-type intervals into raw
 *      windows.
 *   2. Merge same-type windows separated by a small gap (one auto/null
 *      interval). This is what visually joins a SELL band that's broken
 *      by a brief 30-min idle interval where the optimizer wanted the
 *      battery to recover SOC before its next discharge — the user
 *      perceives it as a single sell phase, not three.
 *
 * NOTE on order: an earlier version filtered out short windows BEFORE
 * the merge, which dropped isolated single-interval windows even when
 * they sat between two longer windows of the same type. We now merge
 * first and don't post-filter at all — every interval the controller
 * would fire on is worth rendering, including single 30-min sells. The
 * upstream threshold (per-preset deadband) already filters noise.
 */
function identifyWindows(
  data: Array<{ gridPower: number; batteryPower: number; time: string }>,
  thresholdKw: number,
): Window[] {
  let currentWindow: Window | null = null;
  const maxGap = 1;

  const rawWindows: Window[] = [];
  data.forEach((point, index) => {
    const type = classifyInterval(point.batteryPower, point.gridPower, thresholdKw);

    if (type) {
      if (!currentWindow || currentWindow.type !== type) {
        if (currentWindow) {
          rawWindows.push(currentWindow);
        }
        currentWindow = { startIndex: index, endIndex: index, type };
      } else {
        currentWindow.endIndex = index;
      }
    } else if (currentWindow) {
      rawWindows.push(currentWindow);
      currentWindow = null;
    }
  });

  if (currentWindow) {
    rawWindows.push(currentWindow);
  }

  // Bridge same-type windows separated by ≤ maxGap auto intervals.
  const merged: Window[] = [];
  for (const window of rawWindows) {
    const lastWindow = merged[merged.length - 1];
    if (lastWindow && lastWindow.type === window.type) {
      const gap = window.startIndex - lastWindow.endIndex - 1;
      if (gap <= maxGap) {
        lastWindow.endIndex = window.endIndex;
        continue;
      }
    }
    merged.push({ ...window });
  }

  return merged;
}

export function BatteryPlanChart({
  data,
  className = '',
  optimizationMode,
  ratedDischargeW,
  priceProvider,
  region,
  freeChargeWindow,
  happyHourWindow,
}: BatteryPlanChartProps) {
  const thresholdKw = thresholdKwForMode(optimizationMode, ratedDischargeW);
  const tz = regionToTimezone(region);
  // Track visibility of each series
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    windows: true,
    batteryPower: true,
    evPower: true,
    soc: true,
    solar: false, // Hidden by default
    buyPrice: true,
    sellPrice: true,
  });

  const toggleVisibility = (key: SeriesKey) => {
    setVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const chartData = data.map((point) => ({
    time: formatTimeInTz(point.timestamp, tz),
    timestamp: point.timestamp,
    batteryPower: point.battery_power_w / 1000, // Convert to kW
    gridPower: point.grid_power_w / 1000, // Convert to kW - positive = import, negative = export
    solarPower: point.pv_power_w / 1000, // Convert to kW
    evPower: (point.ev_power_w ?? 0) / 1000, // Planned EV charging, kW
    soc: point.soc_percent,
    importPrice: point.import_price,
    exportPrice: point.export_price,
  }));

  // Identify buy/sell windows using the same joint P_batt + P_grid logic
  // and per-preset threshold the battery controller uses.
  const windows = identifyWindows(chartData, thresholdKw);

  // Fixed-plan named windows: GloBird ZeroHero (per-site free charging,
  // self-consumption 6-9pm) and Flow Power Happy Hour (feed-in window).
  const freeCharge = freeChargeWindow ?? GLOBIRD_DEFAULT_FREE_CHARGE;
  const namedWindows =
    priceProvider === 'globird_zerohero'
      ? identifyNamedWindows(chartData, tz, [
          { type: 'free-charge', ...freeCharge },
          {
            type: 'self-consumption',
            startHour: GLOBIRD_SELF_CONSUMPTION.start,
            endHour: GLOBIRD_SELF_CONSUMPTION.end,
          },
        ])
      : priceProvider === 'flowpower_happy_hour'
      ? identifyNamedWindows(chartData, tz, [
          { type: 'happy-hour', ...(happyHourWindow ?? FLOWPOWER_DEFAULT_HAPPY_HOUR) },
        ])
      : [];

  // EV charging overlay: only when the plan actually scheduled an EV
  // (the overwhelmingly common case is no EV — no series, no legend entry).
  const hasEvSchedule = chartData.some((d) => d.evPower > 0);

  // Calculate max values for y-axis domains (include the EV schedule so a
  // 7+ kW charger block isn't clipped by a smaller battery power range)
  const maxPower = Math.max(
    ...chartData.map((d) => Math.max(Math.abs(d.batteryPower), d.evPower)),
  );
  const powerDomain = [-Math.ceil(maxPower), Math.ceil(maxPower)];

  // Calculate price domain for better visualization (include both import and export prices)
  // Include negative prices - negative import price means you get PAID to consume
  const importPrices = chartData.map((d) => d.importPrice).filter((p) => p != null);
  const exportPrices = chartData.map((d) => d.exportPrice).filter((p) => p != null);
  const allPrices = [...importPrices, ...exportPrices];
  const maxPrice = allPrices.length > 0 ? Math.max(...allPrices) : 0.50;
  const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
  // Allow negative prices in domain - important for showing when you get paid to consume
  const priceDomain = [minPrice - 0.05, maxPrice + 0.05];
  const hasExportPrices = exportPrices.filter((p) => p > 0).length > 0;

  return (
    <div className={`card p-4 ${className}`}>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
        Battery Plan
      </h3>
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id="socGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={PLAN_COLORS.soc} stopOpacity={0.3} />
              <stop offset="95%" stopColor={PLAN_COLORS.soc} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="solarGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={PLAN_COLORS.solar} stopOpacity={0.3} />
              <stop offset="95%" stopColor={PLAN_COLORS.solar} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="batteryChargeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={PLAN_COLORS.charge} stopOpacity={0.3} />
              <stop offset="95%" stopColor={PLAN_COLORS.charge} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="batteryDischargeGradient" x1="0" y1="1" x2="0" y2="0">
              <stop offset="5%" stopColor={PLAN_COLORS.sell} stopOpacity={0.3} />
              <stop offset="95%" stopColor={PLAN_COLORS.sell} stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Buy/Sell Window Highlights */}
          {visible.windows && windows.map((window, idx) => {
            // Window colors (as in the app): buy=blue, sell=orange (battery discharge), sell-excess=solar yellow
            const windowColor = window.type === 'buy' ? PLAN_COLORS.charge : window.type === 'sell' ? PLAN_COLORS.sell : PLAN_COLORS.solar;
            const windowLabel = window.type === 'buy' ? 'BUY' : window.type === 'sell' ? 'SELL' : 'SELL EXCESS';
            return (
              <ReferenceArea
                key={`window-${idx}`}
                x1={chartData[window.startIndex]?.time}
                x2={chartData[Math.min(window.endIndex + 1, chartData.length - 1)]?.time}
                yAxisId="power"
                fill={windowColor}
                fillOpacity={0.08}
                stroke={windowColor}
                strokeOpacity={0.3}
                strokeDasharray="3 3"
                label={{
                  value: windowLabel,
                  position: 'insideTop',
                  fill: windowColor,
                  fontSize: 9,
                  fontWeight: 'bold',
                  opacity: 0.6,
                }}
              />
            );
          })}

          {/* Fixed-plan named windows: free charging (teal), self-consumption
              (purple), Happy Hour (gold — distinct from the orange SELL windows
              it usually contains) */}
          {visible.windows && namedWindows.map((w, idx) => {
            const color =
              w.type === 'free-charge' ? PLAN_COLORS.freeCharge
              : w.type === 'happy-hour' ? PLAN_COLORS.happyHour
              : PLAN_COLORS.powerHome;
            const label =
              w.type === 'free-charge' ? 'FREE CHARGE'
              : w.type === 'happy-hour' ? 'HAPPY HOUR'
              : 'SELF-CONSUMPTION';
            return (
              <ReferenceArea
                key={`named-${idx}`}
                x1={chartData[w.startIndex]?.time}
                x2={chartData[Math.min(w.endIndex + 1, chartData.length - 1)]?.time}
                yAxisId="power"
                fill={color}
                fillOpacity={w.type === 'happy-hour' ? 0.14 : 0.07}
                stroke={color}
                strokeOpacity={w.type === 'happy-hour' ? 0.6 : 0.35}
                strokeDasharray="2 2"
                label={{
                  value: label,
                  position: 'insideBottom',
                  fill: color,
                  fontSize: 9,
                  fontWeight: 'bold',
                  opacity: 0.7,
                }}
              />
            );
          })}

          <XAxis
            dataKey="time"
            tick={{ fill: '#9CA3AF', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />

          {/* Left Y-axis for battery power (kW) */}
          <YAxis
            yAxisId="power"
            domain={powerDomain}
            tick={{ fill: '#9CA3AF', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => `${value} kW`}
          />

          {/* Right Y-axis for SOC (%) */}
          <YAxis
            yAxisId="soc"
            orientation="right"
            domain={[0, 100]}
            tick={{ fill: '#9CA3AF', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => `${value}%`}
          />

          {/* Third Y-axis for price */}
          <YAxis
            yAxisId="price"
            orientation="right"
            domain={priceDomain}
            hide
          />

          <Tooltip content={<CustomTooltip thresholdKw={thresholdKw} />} />

          <ReferenceLine y={0} yAxisId="power" stroke="#4B5563" strokeDasharray="3 3" />

          {/* Battery SOC as filled area */}
          {visible.soc && (
            <Area
              yAxisId="soc"
              type="monotone"
              dataKey="soc"
              name="Battery SOC"
              stroke={PLAN_COLORS.soc}
              strokeWidth={2}
              fill="url(#socGradient)"
            />
          )}

          {/* Solar Power as filled area (yellow) - hidden by default */}
          {visible.solar && (
            <Area
              yAxisId="power"
              type="monotone"
              dataKey="solarPower"
              name="Solar Forecast"
              stroke={PLAN_COLORS.solar}
              strokeWidth={2}
              fill="url(#solarGradient)"
            />
          )}

          {/* Battery Power as line */}
          {visible.batteryPower && (
            <Line
              yAxisId="power"
              type="stepAfter"
              dataKey="batteryPower"
              name="Battery Power"
              stroke={PLAN_COLORS.charge}
              strokeWidth={2}
              dot={false}
            />
          )}

          {/* Planned EV charging as step line (pink) - only when the plan includes an EV */}
          {hasEvSchedule && visible.evPower && (
            <Line
              yAxisId="power"
              type="stepAfter"
              dataKey="evPower"
              name="EV Charging"
              stroke={PLAN_COLORS.ev}
              strokeWidth={2}
              dot={false}
            />
          )}

          {/* Import Price as line */}
          {visible.buyPrice && (
            <Line
              yAxisId="price"
              type="stepAfter"
              dataKey="importPrice"
              name="Buy Price"
              stroke={PLAN_COLORS.importPrice}
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={false}
              opacity={0.7}
            />
          )}

          {/* Export Price as line (yellow) - only show if data available */}
          {hasExportPrices && visible.sellPrice && (
            <Line
              yAxisId="price"
              type="stepAfter"
              dataKey="exportPrice"
              name="Sell Price"
              stroke={PLAN_COLORS.exportPrice}
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={false}
              opacity={0.7}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Interactive Legend - click to toggle visibility */}
      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        <button
          onClick={() => toggleVisibility('windows')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all ${
            visible.windows
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 line-through'
          } hover:bg-gray-200 dark:hover:bg-gray-600`}
        >
          <div className={`w-3 h-3 rounded border ${visible.windows ? 'bg-[#4b8fe8]/20 border-[#4b8fe8]/50' : 'bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500'}`} />
          <span>Buy/Sell Windows</span>
        </button>
        <button
          onClick={() => toggleVisibility('batteryPower')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all ${
            visible.batteryPower
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 line-through'
          } hover:bg-gray-200 dark:hover:bg-gray-600`}
        >
          <div className={`w-3 h-0.5 ${visible.batteryPower ? 'bg-[#4b8fe8]' : 'bg-gray-300 dark:bg-gray-500'}`} />
          <span>Battery Power</span>
        </button>
        {hasEvSchedule && (
          <button
            onClick={() => toggleVisibility('evPower')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all ${
              visible.evPower
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 line-through'
            } hover:bg-gray-200 dark:hover:bg-gray-600`}
          >
            <div className={`w-3 h-0.5 ${visible.evPower ? 'bg-[#ec4899]' : 'bg-gray-300 dark:bg-gray-500'}`} />
            <span>EV Charging</span>
          </button>
        )}
        <button
          onClick={() => toggleVisibility('soc')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all ${
            visible.soc
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 line-through'
          } hover:bg-gray-200 dark:hover:bg-gray-600`}
        >
          <div className={`w-3 h-3 rounded ${visible.soc ? 'bg-[#25a35a]/30' : 'bg-gray-200 dark:bg-gray-600'}`} />
          <span>SOC %</span>
        </button>
        <button
          onClick={() => toggleVisibility('solar')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all ${
            visible.solar
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 line-through'
          } hover:bg-gray-200 dark:hover:bg-gray-600`}
        >
          <div className={`w-3 h-3 rounded ${visible.solar ? 'bg-[#f5b83d]/30' : 'bg-gray-200 dark:bg-gray-600'}`} />
          <span>Solar</span>
        </button>
        <button
          onClick={() => toggleVisibility('buyPrice')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all ${
            visible.buyPrice
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 line-through'
          } hover:bg-gray-200 dark:hover:bg-gray-600`}
        >
          <div className={`w-3 h-0.5 ${visible.buyPrice ? 'bg-[#4b8fe8]' : 'bg-gray-300 dark:bg-gray-500'}`} style={{ borderTopStyle: 'dashed' }} />
          <span>Buy Price</span>
        </button>
        <button
          onClick={() => toggleVisibility('sellPrice')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all ${
            visible.sellPrice
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 line-through'
          } hover:bg-gray-200 dark:hover:bg-gray-600`}
        >
          <div className={`w-3 h-0.5 ${visible.sellPrice ? 'bg-[#f26a1b]' : 'bg-gray-300 dark:bg-gray-500'}`} style={{ borderTopStyle: 'dashed' }} />
          <span>Sell Price</span>
        </button>
      </div>
    </div>
  );
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    dataKey: string;
  }>;
  label?: string;
  thresholdKw?: number;
}

function CustomTooltip({ active, payload, label, thresholdKw = 1 }: TooltipProps) {
  if (!active || !payload) return null;

  // Run the same classifier the chart's window highlights use, so the
  // tooltip badge always agrees with the band the user is hovering over.
  const gridEntry = payload.find((p) => p.dataKey === 'gridPower');
  const batteryEntry = payload.find((p) => p.dataKey === 'batteryPower');
  const gridPower = gridEntry?.value || 0;
  const batteryPower = batteryEntry?.value || 0;

  const classified = classifyInterval(batteryPower, gridPower, thresholdKw);
  const windowType: string =
    classified === 'buy'
      ? 'Buy Window'
      : classified === 'sell'
      ? 'Sell Window'
      : classified === 'sell-excess'
      ? 'Sell Excess Solar'
      : 'Idle';

  return (
    <div className="card p-3">
      <div className="flex items-center justify-between gap-4 mb-2">
        <p className="font-medium text-gray-900 dark:text-white">{label}</p>
        <span className={`text-xs px-1.5 py-0.5 rounded ${
          windowType === 'Buy Window'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
            : windowType === 'Sell Window'
            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
            : windowType === 'Sell Excess Solar'
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        }`}>
          {windowType}
        </span>
      </div>
      {payload
        // Hide the EV entry on the (many) intervals where the step schedule
        // is at zero — no point cluttering the tooltip with "EV Charging: 0 W".
        .filter((entry) => entry.dataKey !== 'evPower' || entry.value > 0)
        .map((entry, index) => (
        <div key={index} className="flex items-center gap-2 text-sm">
          <div
            className="w-3 h-3 rounded"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-600 dark:text-gray-300">{entry.name}:</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {formatValue(entry.dataKey, entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatValue(dataKey: string, value: number): string {
  switch (dataKey) {
    case 'batteryPower':
    case 'gridPower':
    case 'solarPower':
    case 'evPower':
      // Convert kW back to W for tooltip display
      return `${(value * 1000).toFixed(0)} W`;
    case 'soc':
      return `${value.toFixed(0)}%`;
    case 'importPrice':
    case 'exportPrice':
      return `${(value * 100).toFixed(1)}c/kWh`;
    default:
      return value.toFixed(2);
  }
}
